import { useMemo } from "react";
import { filterAmenitiesOptions, IFilters } from "./Filters/Filters.def";
import { getHarExtraFilterEntries, KnownExtraFilter } from "./Filters/HarExtraFilters.def";
import { isString } from "lodash";
import { currencyFormatter, numberFormatter, getLongValue } from "utils/helpers";

import { mlsStatusLoggedinOptions, parkingSpacesOptions, financeOptions, maintenanceFeeOptions, propertyTypeOptions, propertyStatusSaleOptions, constructionLookup } from "utils/filtersData";
import { SingleOrArray } from "types/utility";
import { ILabeledField } from "operations/search-filter-operation";

interface IFilterPillsProps {
    filters: IFilters;
}

/**
 * A filter a pill's visibility may depend on: either a modeled `IFilters` key,
 * or one of the HAR.com passthrough params addressed as `extra.<name>`.
 *
 * The template-literal half is the point — `extra` is an untyped
 * `Record<string, string>`, so `'extra.drivetime_centre'` would otherwise be a
 * rule that quietly never matches. Restricting the suffix to
 * `KnownExtraFilter` turns that typo into a compile error.
 */
type FilterRef = keyof IFilters | `extra.${KnownExtraFilter}`;

interface IConfigurationItemBase {
    /** Suppress this pill while any of these filters is present. */
    hideWhen?: readonly FilterRef[];
    /** Escape hatch for a rule a presence list can't express — one that depends
     *  on another filter's VALUE rather than merely its presence. */
    when?: (filters: IFilters) => boolean;
}

interface ISingleConfigurationItem<K extends keyof IFilters = keyof IFilters> extends IConfigurationItemBase {
    filter: K
    value?: IFilters[K] | IFilters[K][] | RegExp;
    label?: string | ((value?: IFilters[K]) => string);
}

interface IRangeConfigurationItem<K1 extends keyof IFilters = keyof IFilters, K2 extends keyof IFilters = keyof IFilters> extends IConfigurationItemBase {
    filter: [K1, K2]
    label?: string | ((value1?: IFilters[K1], value2?: IFilters[K2]) => string);
}

/** A pill built from an arbitrary number of filters, for criteria that only make
 *  sense as a set (the four map-bound corners). A range is the 2-key special
 *  case; this is the general form. */
interface IGroupConfigurationItem<K extends keyof IFilters = keyof IFilters> extends IConfigurationItemBase {
    filter: K[];
    label?: string | ((...values: Array<IFilters[K] | undefined>) => string);
    /** Emit the pill only when EVERY key in the group holds a value. */
    requireAll?: boolean;
}

type IConfigurationItem = ISingleConfigurationItem | IRangeConfigurationItem | IGroupConfigurationItem;

const configuration: IConfigurationItem[] = [];

registerConfiguration({ filter: 'neighborhood' });
registerConfiguration({ filter: 'Address' });
registerConfiguration({ filter: 'ZipCode' });
registerConfiguration({ filter: 'City', label: value => `City: ${value}` });
registerConfiguration({
    filter: 'County',
    // County holds a labeled field; prefer its resolved name and fall back to
    // the FIPS code so an unknown county still renders a pill.
    label: value => `County: ${value?.label || value?.value || ''}`
});
registerConfiguration({ filter: 'Subdivisions' });
// Map-drawn geo scope. It has no filter-modal control — Filters.tsx carries it in
// PASSTHROUGH_FILTER_KEYS and replays it verbatim — so the pill is the only place
// the user sees that the search is restricted to an area they selected on the map.
// HAR.com always sends the four corners together; a partial set can't describe a
// box, hence `requireAll`. Hidden on a drive-time search: the server discards the
// bounds when drive time is active. That includes a thread persisted before
// CB-1509 (its legacy center is promoted before the pills render): its pills
// describe how its next page runs, which is without the bounds.
registerConfiguration({
    filter: ['map_tools_nwlat', 'map_tools_nwlng', 'map_tools_selat', 'map_tools_selng'],
    requireAll: true,
    label: 'Map Bounds',
    hideWhen: ['drivetime_address', 'drivetime_center'],
});
// The bounds and the drawn polygon are INDEPENDENT filters that co-exist (the
// polygon is the drawn region, the bounds a zoomed-in subset of it), so this is a
// separate pill and never merged into the one above.
//
// Hidden on a drive-time search: HAR.com computes an isochrone from the
// drive-time criteria and sends it as `map_tools_polygon` alongside
// `drivetime_center`, so the polygon alone can't tell a shape the user drew from
// one HAR.com derived for them, and "Shape" would misdescribe the search. The
// Drive Time pill below is what describes it. The address covers the request
// as sent (the center only exists once the server has geocoded it). Artifacts
// persisted before CB-1509 carry the center inside `extra`; the converters that
// feed every pill render promote it onto the modeled key first
// (promoteLegacyDriveTime), so the modeled keys cover those too.
registerConfiguration({
    filter: 'map_tools_polygon',
    label: 'Shape',
    hideWhen: ['drivetime_address', 'drivetime_center'],
});
// Drive-time search (har.com's "Drive Time" location mode). One pill for the
// whole criteria set, labelled exactly as har.com's own pill is; the details
// (destination, arrival time, travel time) are read and edited in the Filters
// modal. Either key alone is enough: a search seeded from a HAR.com URL may
// carry only the center, a fresh one from the modal only the address until the
// server geocodes it.
registerConfiguration({
    filter: ['drivetime_address', 'drivetime_center'],
    label: 'Drive Time',
});
registerConfiguration({
    filter: 'PropertyType',
    label: value => getLongValue(propertyTypeOptions, value) || ''
});
registerConfiguration({
    filter: 'amenities',
    label: value => getLongValue(filterAmenitiesOptions, value) || ''
});
registerConfiguration({
    filter: 'Category',
    label: value => getLongValue(propertyStatusSaleOptions, value) || ''
});
registerConfiguration({
    filter: 'HoafeeMax',
    label: value => {
        const label = getLongValue(maintenanceFeeOptions, value) || '';
        return label.replace('month', 'mo').replace('year', 'yr');
    }
});
registerConfiguration({ filter: 'Parking', label: value => getLongValue(parkingSpacesOptions, value) || '' });
registerConfiguration({
    filter: 'Finance',
    // Fall back to the raw value (as County does above) so a finance option that
    // is not in `financeOptions` still renders a pill instead of disappearing.
    label: value => getLongValue(financeOptions, value, isString(value) ? value : '') || ''
});
registerConfiguration({ filter: 'Status', label: value => getLongValue(mlsStatusLoggedinOptions, value) || '' });
registerConfiguration({
    filter: 'NewConstr',
    // label: (value?: string | string[]) => transformFilterPill(value, constructionObject)
    label: value => getLongValue(constructionLookup, value) || ''
});
registerConfiguration({ filter: ['MinimumPrice', 'MaximumPrice'], label: rangePriceValue() });
registerConfiguration({ filter: ['BedroomMin', "BedroomMax"], label: rangeBedOrBathValue('Beds') });
registerConfiguration({ filter: ['FullbathMin', 'FullbathMax'], label: rangeBedOrBathValue('Baths') });
registerConfiguration({ filter: 'HalfbathAvailability', label: withOrNoLabel('Half Bath') });
registerConfiguration({ filter: 'Stories', label: value => `${value} Story` });
registerConfiguration({ filter: 'school_district' });
registerConfiguration({ filter: 'elemantary_school' });
registerConfiguration({ filter: 'middle_school' });
registerConfiguration({ filter: 'high_school' });
registerConfiguration({ filter: 'KeyMap' });
registerConfiguration({ filter: 'For' });
registerConfiguration({ filter: 'nid' });
registerConfiguration({ filter: 'MLSNumber', label: value => `MLS #: ${value}` });
registerConfiguration({
    filter: 'community',
    label: (value) => {
        const normalizedValue = Array.isArray(value) ? value[0] : value;
        return `Community: ${normalizedValue?.label || ''}`;
    }
});
registerConfiguration({ filter: 'marketarea' });
registerConfiguration({ filter: 'MLSArea' });
registerConfiguration({ filter: 'Style' });
registerConfiguration({ filter: ['GarageNum', 'GarageDesc'], label: rangeGarageValue('Garage') });
registerConfiguration({ filter: ['buildingSQFTMin', 'buildingSQFTMax'], label: rangeBuildingOrLotSqftValue('Building Sqft') });
registerConfiguration({ filter: ['PricesqftMin', 'PricesqftMax'], label: rangePriceValue('Price/Sqft') });
registerConfiguration({ filter: ['LotAcresMin', 'LotAcresMax'], label: rangeLotAcresValue('Lot (Acres)') });
registerConfiguration({ filter: ['YearbuildsMin', 'YearbuildsMax'], label: rangeValue('Year Built') });
registerConfiguration({ filter: ['LotsizeMin', 'LotsizeMax'], label: rangeBuildingOrLotSqftValue('Lot (Sqft)') });

registerConfiguration({
    filter: 'extra', label: (value) => {
        if (!value || typeof value !== 'object') return '';
        // Count what the "HAR.com extra filters" section lists, not the raw bag:
        // the passthrough blob carries blank query-string keys and paging
        // plumbing, and counting those made the pill disagree with the panel.
        const total = getHarExtraFilterEntries(value as Record<string, string>).length;
        if (total === 0) return '';

        return `+ ${total} HAR.com ${total === 1 ? 'filter' : 'filters'}`;
    }
});

export const stringIntoArray = <T extends string,>(value: T | T[] | undefined): T[] => {
    if (!value) return [];
    return Array.isArray(value) ? value : value.toString().split(',') as T[];
}

export function FilterPills(props: IFilterPillsProps) {
    const { filters } = props;

    // Derived during render, NOT in a useEffect writing to state. The effect
    // version painted an empty chip box on the first frame of every mount and
    // only filled it in on the following commit, so anything that remounted this
    // component made the bubble visibly blank and refill (CB-1780).
    const pills = useMemo(() => {
        const { amenities, Category, Stories, ...remainingFilters } = filters;
        const parsedFilters = {
            ...remainingFilters,
            amenities: stringIntoArray(amenities),
            Category: stringIntoArray(Category),
            Stories: stringIntoArray(Stories),
        };

        return translateAll(parsedFilters || {});
    }, [filters]);

    return (
        <div className="user_bubble__wraper filters_bubble">
            <div className='position-relative py-3 px-3 align-items-center bg_color_cement_light flex-column' style={{ borderRadius: '14px', display: 'inline-flex', maxWidth: '920px', minWidth: '300px' }}>
                {/* <div className='font_weight--semi_bold font_size--medium pr-2 align-self-start text-nowrap pb-2'>Filter By:</div> */}
                <div className='d-flex flex-wrap mr-auto ml-0' style={{ rowGap: '0.5rem' }}>
                    {pills.map((pill, index) => <div key={index} className="btn btn--ordinary btn--small btn--filters_pills mr-2 mb-md-0 mb-2">{pill}</div>)}
                </div>
            </div>
        </div>
    );
}

const EXTRA_REF_PREFIX = "extra." as const;

const isExtraRef = (ref: FilterRef): ref is `extra.${KnownExtraFilter}` =>
    ref.startsWith(EXTRA_REF_PREFIX);

/**
 * Presence test for a `FilterRef`. `extra` values are raw query-string strings,
 * so an empty or whitespace-only one counts as absent — HAR.com emits a bare
 * `drivetime_center=` once the drive-time criteria have been cleared, and that
 * must not keep a dependent pill hidden.
 */
export function isFilterPresent(filters: IFilters, ref: FilterRef): boolean {
    // The bag as a whole: defer to the helper the passthrough section and its
    // "+ N HAR.com filters" count already use, so a rule on `extra` can never
    // disagree with what the user is shown. `convertNativeToFilters` hands the
    // leftover native object over as-is, so the bag routinely holds blank
    // query-string keys and paging/sort plumbing, none of which narrow a search.
    if (ref === 'extra') { return getHarExtraFilterEntries(filters.extra).length > 0; }

    // `as string`: with KNOWN_EXTRA_FILTERS empty the extra branch narrows to
    // `never`; the resolution stays in place for the next passthrough key.
    const value = isExtraRef(ref)
        ? filters.extra?.[(ref as string).slice(EXTRA_REF_PREFIX.length)]
        : filters[ref];

    if (value === undefined || value === null) { return false; }
    if (Array.isArray(value)) { return value.length > 0; }
    if (typeof value === 'string') { return value.trim() !== ''; }
    return true;
}

/**
 * Cross-filter visibility. Evaluated before any value extraction, so it applies
 * to single, range and group configurations alike, and so a hidden pill costs
 * nothing to skip.
 */
export function isConfigurationVisible(config: IConfigurationItem, filters: IFilters): boolean {
    if (config.hideWhen?.some(ref => isFilterPresent(filters, ref))) { return false; }
    return config.when ? config.when(filters) : true;
}

function translateAll(filters: IFilters): string[] {
    return configuration.reduce((pills, config) => {

        if (!isConfigurationVisible(config, filters)) { return pills; }

        // Getting filter values
        const keys = config.filter instanceof Array ? config.filter : [config.filter];
        // const values = keys.map(key => filters[ky]);
        const values = keys.map(key => {
            const value = filters[key];
            // Convert numbers to strings to maintain type compatibility
            return typeof value === 'number' ? String(value) : value;
        });
        if (!values.some(value => !!value)) { return pills; }

        // For Range / Group
        if (config.filter instanceof Array) {
            // A group flagged `requireAll` describes a criterion only its complete
            // set can express (a bounding box needs all four corners), so a partial
            // set renders nothing rather than a misleading pill.
            if ('requireAll' in config && config.requireAll && !values.every(value => !!value)) { return pills; }
            const label = generateLabel(config, values);
            // Guard the empty label the way the single-value branch below does —
            // otherwise a label function that opts out renders a blank pill.
            if (!label) { return pills; }
            return [...pills, label];
        }

        const items = values.flat().filter(x => !!x);
        if (!items.length) { return pills; }

        const matchesValue = 'value' in config ? items.some(value => matchesConfigValue(config, value)) : undefined;
        if (matchesValue === false) { return pills; }

        const labels = items.map(value => generateLabel(config, [value as IFilters[keyof IFilters]])).filter(x => !!x);
        return [...pills, ...labels];

    }, [] as string[]);
}

function matchesConfigValue(item: ISingleConfigurationItem, value?: SingleOrArray<string | ILabeledField<string | number>> | Record<string, string>): boolean {
    if (Array.isArray(value)) { return value.some(value => matchesConfigValue(item, value)); }
    if (!value) { return false; }
    value = typeof value === 'object' && 'value' in value ? value.value?.toString() : value;
    // A Record<string, string> has no scalar to compare against `item.value`, so
    // treat it as a match rather than suppressing the pill outright. (Nothing
    // registers a `value` predicate today; this is the conservative default for
    // the first config that does.)
    if (typeof value === 'object') { return true; }
    if (item.value instanceof RegExp) { return item.value.test(value); }
    return item.value === value;
}

function generateLabel(item: IConfigurationItem, fieldsValues: Array<IFilters[keyof IFilters]>): string {
    const values = fieldsValues.flat();
    if (!item.label) { return values.map(toLabel).filter(x => !!x).join(', '); }
    else if (isString(item.label)) { return item.label; }
    return item.label(...values as IFilters[keyof IFilters][]);
}

function toLabel(value?: string | number | ILabeledField<string> | ILabeledField<number> | Record<string, string> | undefined): string | undefined {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string' || typeof value === 'number') return value.toString();
    if (typeof value === 'object' && 'label' in value) return value.label?.toString() || value.value?.toString();
    return undefined;
}

function rangePriceValue(label?: string) {
    const filterLabel = label || '';
    if (filterLabel) {
        return (min?: string, max?: string) => {
            if (min && max) { return `$${min} - $${max} ${filterLabel}`; }
            if (min) { return `Min $${min} ${filterLabel}` }
            if (max) { return `Max $${max} ${filterLabel}`; }
            return '';
        }
    }
    return (min?: string, max?: string) => {
        if (min && max) { return `${currencyFormatter(+min)} - ${currencyFormatter(+max)}`; }
        if (min) { return `Min ${currencyFormatter(+min)}` }
        if (max) { return `Max ${currencyFormatter(+max)}`; }
        return '';
    }
}

function rangeBedOrBathValue(label: string) {
    return (min?: string, max?: string) => {
        if (min && max) { return `${min} - ${max} ${label}`; }
        if (min) { return `${min}+ ${label}` }
        if (max) { return `${max}+ ${label}`; }
        return '';
    }
}

function rangeBuildingOrLotSqftValue(label: string) {
    return (min?: string, max?: string) => {
        if (min && max) { return `${numberFormatter(+min)} - ${numberFormatter(+max)} ${label}`; }
        if (min) { return `Min ${numberFormatter(+min)} ${label}` }
        if (max) { return `Max ${numberFormatter(+max)} ${label}`; }
        return '';
    }

}

function rangeLotAcresValue(label: string) {
    return (min?: string, max?: string) => {
        if (min && max) { return `${min} Acres - ${max} Acres ${label}`; }
        if (min) { return `Min ${min} Acres ${label}` }
        if (max) { return `Max ${max} Acres ${label}`; }
        return '';
    }
}

function rangeGarageValue(label: string) {
    return (min?: string, max?: string) => {
        if (min && max) { return `${min}+ ${max} ${label}`; }
        if (min) { return `${min}+ ${label}` }
        if (max) { return `w/ ${max} ${label}`; }
        return '';
    }
}

function rangeValue(label: string) {
    return (min?: string, max?: string) => {
        const isLabelConatinsPrice = label.toLowerCase().includes('price');
        const dollarSymbol = isLabelConatinsPrice ? '$' : '';
        if (min && max) { return `${dollarSymbol}${min} - ${dollarSymbol}${max} ${label}`; }
        if (min) { return `Min ${dollarSymbol}${min} ${label}` }
        if (max) { return `Max ${dollarSymbol}${max} ${label}`; }
        return '';
    }
}

function withOrNoLabel(label: string) {
    return (value?: string | number) => {
        const val = typeof value === 'number' ? String(value) : value;
        if (val === '1') { return `With ${label}`; }
        if (val === '0') { return `No ${label}`; }
        return '';
    }
}

function registerConfiguration<K1 extends keyof IFilters, K2 extends keyof IFilters>(config: IRangeConfigurationItem<K1, K2>): void;
function registerConfiguration<K extends keyof IFilters>(config: IGroupConfigurationItem<K>): void;
function registerConfiguration<K extends keyof IFilters>(config: ISingleConfigurationItem<K>): void;
function registerConfiguration(config: IConfigurationItem): void {
    configuration.push(config);
}